import { useEffect, useRef, useState } from "react";
import type { Message } from "@cloudflare/realtimekit";
import { getLivestreamChatText } from "./useLivestreamChat";

const OVERLAY_DURATION_MS = 7000;

type OverlayItem = {
  id: string;
  displayName: string;
  text: string;
};

export function LivestreamChatOverlay({ messages }: { messages: Message[] }) {
  const [overlayItems, setOverlayItems] = useState<OverlayItem[]>([]);
  const seenIdsRef = useRef(new Set<string>());

  useEffect(() => {
    for (const message of messages) {
      if (seenIdsRef.current.has(message.id)) continue;
      const text = getLivestreamChatText(message);
      if (!text) continue;

      seenIdsRef.current.add(message.id);
      const item: OverlayItem = {
        id: message.id,
        displayName: message.displayName,
        text,
      };

      setOverlayItems((prev) => [...prev.slice(-24), item]);

      window.setTimeout(() => {
        setOverlayItems((prev) => prev.filter((entry) => entry.id !== item.id));
      }, OVERLAY_DURATION_MS);
    }
  }, [messages]);

  if (overlayItems.length === 0) return null;

  return (
    <div
      className="pointer-events-none absolute bottom-20 left-3 right-16 z-40 flex max-h-[45%] flex-col justify-end gap-1.5 overflow-hidden sm:left-4 sm:bottom-24"
      aria-live="polite"
    >
      {overlayItems.map((item) => (
        <div
          key={item.id}
          className="livestream-chat-bubble max-w-[85%] self-start rounded-2xl bg-black/35 px-3 py-1.5 text-sm text-white shadow-[0_1px_8px_rgba(0,0,0,0.45)] backdrop-blur-sm"
        >
          <span className="font-semibold">{item.displayName}</span>
          <span className="opacity-90"> {item.text}</span>
        </div>
      ))}
      <style>{`
        @keyframes livestream-chat-fade {
          0%, 55% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .livestream-chat-bubble {
          animation: livestream-chat-fade ${OVERLAY_DURATION_MS}ms ease-out forwards;
        }
      `}</style>
    </div>
  );
}
